import { StockItemDto } from '@nest/domain-entities-metadata-generated';
import { Button, Textbox, useScanner, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTerminalClient } from '../client-rpc-router';
import { StockItemSelectDialog } from './StockItemSelectDialog';

interface StockItemScanInputProps {
	value?: StockItemDto | null;
	onChange?: (value: StockItemDto | null) => void;
	placeholder?: string;
	autoFocus?: boolean;
	disabled?: boolean;
	className?: string;
}

export interface StockItemScanInputRef {
	reset: () => void;
}

export const StockItemScanInput = forwardRef<StockItemScanInputRef, StockItemScanInputProps>(
	({ value, onChange, placeholder = 'Naskenujte položku...', autoFocus, disabled, className, ...props }, ref) => {
		const client = useTerminalClient();
		const scanner = useScanner();
		const [code, setCode] = useState<string>('');
		const [stockItem, setStockItem] = useState<StockItemDto | null | undefined>();
		const [isEditing, setIsEditing] = useState<boolean>(!value);

		useEffect(() => {
			setStockItem(value);
			setIsEditing(!value);
		}, [value]);

		const scanStockItemMutation = useMutation({
			mutationFn: (code: string) => client.wms.findStockItemByCodeQuery.execute({ code }),
			onSuccess: result => {
				if (result) {
					onChange?.(result);
					// setStockItem(result);
					setCode('');
					setIsEditing(false);
				} else {
					alert('Neznamy kod');
				}
			},
			onError: error => {
				console.error('Načtení položky', error);
			}
		});

		useValidationResultDialog([scanStockItemMutation.error]);

		useEffect(() => {
			if (isEditing && scanner.code && !disabled) {
				handleScan(scanner.code);
			}
		}, [scanner.code, isEditing, disabled]);

		const handleScan = (scannedCode: string) => {
			setCode(scannedCode);
			scanStockItemMutation.mutate(scannedCode);
		};

		const handleKeyEnter = (inputValue: string) => {
			if (inputValue.trim()) {
				scanStockItemMutation.mutate(inputValue.trim());
			}
		};

		const reset = () => {
			onChange?.(null);
			setCode('');
			setIsEditing(true);
		};

		useImperativeHandle(ref, () => ({ reset }));

		const [isDialogOpen, setIsDialogOpen] = useState(false);

		return (
			<div className="flex gap-2 items-center w-full">
				{!isEditing && stockItem ? (
					<div className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2">
						<div className="text-nowrap text-ellipsis overflow-hidden">{stockItem.item.name}</div>
						<Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="ml-auto">
							Změnit
						</Button>
					</div>
				) : (
					<>
						<Textbox
							value={code}
							onChange={e => setCode(e.target.value)}
							placeholder={placeholder || 'Naskenujte položku...'}
							autoFocus={autoFocus}
							disabled={disabled || scanStockItemMutation.isPending}
							onKeyDown={e => {
								if (e.key === 'Enter') {
									e.preventDefault();
									handleScan(code);
								}
							}}
						/>

						<Button type="button" variant="outline" size="sm" onClick={() => setIsDialogOpen(true)} disabled={disabled}>
							Vybrat
						</Button>
					</>
				)}

				<StockItemSelectDialog
					open={isDialogOpen}
					onOpenChange={setIsDialogOpen}
					onSelect={si => {
						onChange?.(si);
						setIsDialogOpen(false);
						setIsEditing(false);
					}}
				/>
			</div>
		);
	}
);

StockItemScanInput.displayName = 'StockItemScanInput';
